import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalService } from 'ng-zorro-antd/modal';
import { FlowExecutorService } from '../../conciliadora/services/flow-executor.service';
import { FlowMonitorService } from '../../conciliadora/services/flow-monitor.service';
import {
  FlowExecutionStep,
  FlowJobStatusType,
  FlowMonitorState,
} from '../../conciliadora/models/conciliadora.models';
import { MultiJobMonitorState } from '../../conciliadora/models/flow-executor.models';

interface PhaseMessage {
  fromMs: number;
  text: string;
}

/** Origem dos dados do monitor: legacy (1 job via FlowMonitorService) ou executor (multi-job). */
export type MonitorSource = 'legacy' | 'executor';

/**
 * Drawer lateral direito que monitora a execucao assincrona de um fluxo
 * NEWMODE. Consome o estado reativo `FlowMonitorService.state$` e renderiza:
 *
 * - Header com titulo, status badge, cronometro e botao fullscreen
 * - Stats (linhas, idconf, idjob)
 * - Progress indeterminado enquanto RUNNING + mensagem dinamica por tempo
 * - Timeline de steps (so aparece quando termina, lendo flowExecutionList)
 * - Painel detalhe do step selecionado (input/output/error)
 * - Footer com botoes Parar (durante) e Fechar (apos terminar)
 *
 * Visibilidade: controlada pelo `@Input() visible`. Componente nao gerencia
 * own state de abertura - segue o pai.
 */
@Component({
  standalone: false,
  selector: 'app-flow-execution-monitor',
  templateUrl: './flow-execution-monitor.component.html',
  styleUrls: ['./flow-execution-monitor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FlowExecutionMonitorComponent implements OnInit {
  private destroyRef = inject(DestroyRef);

  @Input() visible = false;

  /**
   * Origem dos dados:
   * - 'legacy': consome FlowMonitorService.state$ (1 job NEWMODE async).
   * - 'executor': consome FlowExecutorService.aggregate$ + jobState$(jobId)
   *   (multi-job, com sidebar quando >1 job).
   */
  @Input() source: MonitorSource = 'legacy';

  /**
   * Classe(s) extra aplicada(s) na wrap do nz-drawer (teleportado pro body).
   * Aditivo: default vazio = nenhuma mudanca pros consumidores atuais
   * (inclusive a conciliadora original/clara). A versao C passa 'cc-monitor'
   * para escopar a tematizacao DARK do drawer teleportado.
   */
  @Input() wrapClass = '';

  /** Progresso real vindo do pai (poll do banco, acao 'localizar' ou 'processar'). */
  @Input() liveProcessed: number | null = null;
  @Input() liveTotal: number | null = null;
  @Input() livePercent: number | null = null;

  /** Emitido quando o usuario fecha o drawer manualmente. */
  @Output() closed = new EventEmitter<FlowMonitorState>();

  /** Emitido sempre que o estado do monitor muda (para o pai reagir, ex: refresh grid). */
  @Output() stateChanged = new EventEmitter<FlowMonitorState>();

  /** Emitido quando o agregado muda (multi-job). Util para mostrar contadores na barra do pai. */
  @Output() aggregateChanged = new EventEmitter<MultiJobMonitorState | null>();

  state: FlowMonitorState = this.emptyState();
  selected: FlowExecutionStep | null = null;
  fullscreen = false;
  stopping = false;

  // ------- Multi-job (source = 'executor') -------
  aggregate: MultiJobMonitorState | null = null;
  jobsList: FlowMonitorState[] = [];
  selectedJobId: string | null = null;
  private jobSub: Subscription | null = null;

  private readonly phaseMessages: PhaseMessage[] = [
    { fromMs: 0, text: 'Iniciando fluxo no servidor...' },
    { fromMs: 5_000, text: 'Executando steps do fluxo...' },
    { fromMs: 30_000, text: 'Operacao em andamento. Fluxos com muitos registros podem demorar.' },
    { fromMs: 60_000, text: 'Continua processando. Aguarde mais alguns instantes...' },
    { fromMs: 180_000, text: 'Operacao demorada (> 3 min). Considere acompanhar pelo log do servidor se necessario.' },
  ];

  constructor(
    private monitor: FlowMonitorService,
    private executor: FlowExecutorService,
    private message: NzMessageService,
    private modal: NzModalService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    if (this.source === 'executor') {
      this.subscribeExecutor();
    } else {
      this.subscribeLegacy();
    }
  }

  /** Modo legado: 1 job NEWMODE async via FlowMonitorService. */
  private subscribeLegacy(): void {
    this.monitor.state$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((state) => {
      const wasTerminal = this.isTerminal(this.state.status);
      this.state = state;

      if (this.isTerminal(state.status) && !wasTerminal) {
        this.onJobFinished(state);
      }
      if (!state.steps?.length) {
        this.selected = null;
      }
      this.stateChanged.emit(state);
      this.cd.markForCheck();
    });
  }

  /** Modo executor: assina aggregate$ e atualiza lista + state selecionado. */
  private subscribeExecutor(): void {
    this.executor.aggregate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((agg) => {
      this.aggregate = agg;
      this.aggregateChanged.emit(agg);
      if (!agg) {
        this.jobsList = [];
        this.state = this.emptyState();
        this.selectedJobId = null;
        this.jobSub?.unsubscribe();
        this.jobSub = null;
        this.cd.markForCheck();
        return;
      }
      // Snapshot da lista de jobs para a sidebar
      this.jobsList = agg.jobIds
        .map((id) => {
          const obs = this.executor.jobState$(id);
          if (!obs) return null;
          let snapshot: FlowMonitorState | null = null;
          obs.subscribe((s) => (snapshot = s)).unsubscribe();
          return snapshot;
        })
        .filter((s): s is FlowMonitorState => !!s);

      // Auto-seleciona primeiro job em execucao ou o primeiro da lista
      if (!this.selectedJobId || !this.jobsList.find((j) => j.jobId === this.selectedJobId)) {
        const firstRunning = this.jobsList.find(
          (j) => j.status === 'RUNNING' || j.status === 'PENDING'
        );
        this.selectedJobId = (firstRunning ?? this.jobsList[0])?.jobId ?? null;
        this.subscribeSelectedJob();
      }

      // Quando todos terminam, dispara mensagem agregada
      if (
        agg.status === 'COMPLETED' || agg.status === 'ERROR' ||
        agg.status === 'STOPPED' || agg.status === 'PARTIAL'
      ) {
        this.onAggregateFinished(agg);
      }
      this.cd.markForCheck();
    });
  }

  private subscribeSelectedJob(): void {
    this.jobSub?.unsubscribe();
    if (!this.selectedJobId) return;
    const obs = this.executor.jobState$(this.selectedJobId);
    if (!obs) return;
    this.jobSub = obs.subscribe((s) => {
      this.state = s;
      if (!s.steps?.length) this.selected = null;
      this.stateChanged.emit(s);
      this.cd.markForCheck();
    });
  }

  /** Chamado pela UI ao clicar num job da sidebar. */
  selectJob(jobId: string): void {
    if (this.selectedJobId === jobId) return;
    this.selectedJobId = jobId;
    this.selected = null;
    this.subscribeSelectedJob();
  }

  /** Quantos jobs (executor mode). */
  get jobsCount(): number {
    return this.aggregate?.totals.jobs ?? 0;
  }

  /** Mostra sidebar apenas quando >1 job no executor. */
  get showSidebar(): boolean {
    return this.source === 'executor' && this.jobsCount > 1;
  }

  /** Header agregado (executor mode). */
  get aggregateHeader(): string {
    const a = this.aggregate;
    if (!a) return '';
    const done = a.totals.completed + a.totals.failed + a.totals.cancelled;
    return `${done}/${a.totals.jobs} jobs | ${a.totals.rowsOk}/${a.totals.rowsCount} reg. OK${a.totals.rowsErr ? ' | ' + a.totals.rowsErr + ' erros' : ''}`;
  }

  jobStatusColor(job: FlowMonitorState): string {
    switch (job.status) {
      case 'PENDING': return 'blue';
      case 'RUNNING': return 'processing';
      case 'COMPLETED': return 'success';
      case 'ERROR': return 'error';
      case 'STOPPED': return 'warning';
      default: return 'default';
    }
  }

  trackJob = (_: number, j: FlowMonitorState): string => j.jobId ?? '';

  private onAggregateFinished(agg: MultiJobMonitorState): void {
    if (agg.status === 'COMPLETED') {
      this.message.success(`Execucao concluida em ${this.formatElapsed(agg.elapsedMs)}.`);
    } else if (agg.status === 'PARTIAL') {
      this.message.warning(
        `Execucao concluida com erros: ${agg.totals.failed} job(s) falharam.`
      );
    } else if (agg.status === 'ERROR') {
      this.message.error('Todos os jobs falharam.');
    } else if (agg.status === 'STOPPED') {
      this.message.warning('Execucao interrompida.');
    }
  }

  private formatElapsed(ms: number): string {
    const total = Math.floor((ms ?? 0) / 1000);
    const min = Math.floor(total / 60);
    const sec = total % 60;
    return min > 0 ? `${min}m ${sec.toString().padStart(2, '0')}s` : `${sec}s`;
  }

  // ----------------------- Header / Computed -----------------------

  get statusLabel(): string {
    switch (this.state.status) {
      case 'PENDING': return 'Aguardando';
      case 'RUNNING': return 'Executando';
      case 'COMPLETED': return 'Concluido';
      case 'ERROR': return 'Erro';
      case 'STOPPED': return 'Interrompido';
      case 'NOT_FOUND': return 'Nao encontrado';
      default: return 'Pronto';
    }
  }

  get statusColor(): string {
    switch (this.state.status) {
      case 'PENDING': return 'blue';
      case 'RUNNING': return 'processing';
      case 'COMPLETED': return 'success';
      case 'ERROR': return 'error';
      case 'STOPPED': return 'warning';
      case 'NOT_FOUND': return 'default';
      default: return 'default';
    }
  }

  get isRunning(): boolean {
    if (this.source === 'executor') {
      return this.aggregate?.status === 'RUNNING' || this.aggregate?.status === 'PENDING';
    }
    return this.state.status === 'RUNNING' || this.state.status === 'PENDING';
  }

  get hasErrors(): boolean {
    if (this.state.error) return true;
    return (this.state.steps ?? []).some((s) => !!s.error);
  }

  get errorsCount(): number {
    return (this.state.steps ?? []).filter((s) => !!s.error).length;
  }

  get elapsedSeconds(): number {
    return Math.floor((this.state.elapsedMs ?? 0) / 1000);
  }

  get elapsedFormatted(): string {
    const total = this.elapsedSeconds;
    const min = Math.floor(total / 60);
    const sec = total % 60;
    return min > 0 ? `${min}m ${sec.toString().padStart(2, '0')}s` : `${sec}s`;
  }

  /** Mensagem dinamica em funcao do tempo decorrido. */
  get phaseMessage(): string {
    if (!this.isRunning) return '';
    const ms = this.state.elapsedMs ?? 0;
    let current = this.phaseMessages[0].text;
    for (const phase of this.phaseMessages) {
      if (ms >= phase.fromMs) current = phase.text;
    }
    return current;
  }

  get drawerWidth(): number | string {
    if (this.fullscreen) return '100%';
    return 560;
  }

  // ----------------------- Steps / Selection -----------------------

  selectStep(step: FlowExecutionStep): void {
    this.selected = this.selected?.key === step.key ? null : step;
  }

  readonly MAX_VISIBLE_STEPS = 200;

  get visibleSteps(): FlowExecutionStep[] {
    const s = this.state?.steps ?? [];
    return s.length > this.MAX_VISIBLE_STEPS ? s.slice(-this.MAX_VISIBLE_STEPS) : s;
  }

  get stepsOverflow(): number {
    return Math.max(0, (this.state?.steps?.length ?? 0) - this.MAX_VISIBLE_STEPS);
  }

  trackStep = (index: number, s: FlowExecutionStep): string => `${index}-${s.key}-${s.dhStart ?? ''}`;

  stepColor(s: FlowExecutionStep): string {
    if (s.error) return 'red';
    return 'green';
  }

  formatJson(value: any): string {
    if (value == null) return '(vazio)';
    if (typeof value === 'string') return value || '(vazio)';
    if (typeof value === 'object' && Object.keys(value).length === 0) return '(vazio)';
    try {
      // Replacer local com WeakSet novo por chamada (evita estado compartilhado
      // entre formatJson(input) e formatJson(output)).
      const seen = new WeakSet();
      const replacer = (_key: string, val: any): any => {
        if (typeof val === 'string' && val.length > 5_000) {
          return val.substring(0, 5_000) + `... (+${val.length - 5_000} chars)`;
        }
        if (val !== null && typeof val === 'object') {
          if (seen.has(val)) return '[circular]';
          seen.add(val);
        }
        return val;
      };
      const json = JSON.stringify(value, replacer, 2);
      const MAX = 50_000;
      if (json && json.length > MAX) {
        return json.substring(0, MAX) + `\n\n... (truncado: ${json.length - MAX} caracteres adicionais)`;
      }
      return json ?? '(vazio)';
    } catch {
      return String(value);
    }
  }

  // ----------------------- Actions -----------------------

  toggleFullscreen(): void {
    this.fullscreen = !this.fullscreen;
  }

  onStop(): void {
    // Modo executor: confirma e cancela todos os jobs do grupo (1 ou N).
    if (this.source === 'executor') {
      const total = this.jobsCount;
      this.modal.confirm({
        nzTitle: total > 1 ? 'Parar todos os jobs?' : 'Parar execucao?',
        nzContent:
          total > 1
            ? `Cancela ${total} jobs em execucao. Registros ja processados permaneceram salvos.`
            : 'O fluxo sera interrompido no servidor. Registros ja processados permaneceram salvos.',
        nzOkText: 'Sim, parar',
        nzOkDanger: true,
        nzCancelText: 'Continuar executando',
        nzOnOk: () => {
          this.stopping = true;
          this.executor.cancelAll();
          // Nao tem callback unificado; o aggregate$ ja vai refletir STOPPED.
          setTimeout(() => {
            this.stopping = false;
            this.cd.markForCheck();
          }, 1500);
        },
      });
      return;
    }

    if (!this.state.idjob) return;
    this.modal.confirm({
      nzTitle: 'Parar execucao do fluxo?',
      nzContent:
        'O fluxo sera interrompido no servidor. Registros ja processados nesta execucao ' +
        'permaneceram salvos; registros pendentes nao serao processados.',
      nzOkText: 'Sim, parar',
      nzOkDanger: true,
      nzCancelText: 'Continuar executando',
      nzOnOk: () => {
        this.stopping = true;
        this.monitor.stop().subscribe({
          next: (ok) => {
            this.stopping = false;
            if (ok) this.message.success('Solicitacao de parada enviada ao servidor.');
            else this.message.warning('Nao foi possivel interromper (job pode ja ter terminado).');
            this.cd.markForCheck();
          },
          error: () => {
            this.stopping = false;
            this.message.error('Falha ao solicitar parada.');
            this.cd.markForCheck();
          },
        });
      },
    });
  }

  onClose(): void {
    if (this.isRunning) {
      this.modal.confirm({
        nzTitle: 'Fechar monitor?',
        nzContent:
          'A execucao continua rodando no servidor. Voce podera acompanhar pela proxima vez ' +
          'que abrir esta tela (sera retomada automaticamente).',
        nzOkText: 'Fechar mesmo assim',
        nzCancelText: 'Continuar acompanhando',
        nzOnOk: () => this.emitClose(),
      });
      return;
    }
    this.emitClose();
  }

  private emitClose(): void {
    this.closed.emit(this.state);
    this.fullscreen = false;
    this.selected = null;
  }

  private onJobFinished(state: FlowMonitorState): void {
    if (state.status === 'COMPLETED' && !state.error) {
      this.message.success(`Fluxo concluido em ${this.elapsedFormatted}.`);
    } else if (state.status === 'ERROR') {
      this.message.error(state.errorMessage ?? 'Falha na execucao do fluxo.');
    } else if (state.status === 'STOPPED') {
      this.message.warning('Execucao interrompida.');
    }
  }

  private isTerminal(s: FlowJobStatusType | 'IDLE' | 'PARTIAL'): boolean {
    return (
      s === 'COMPLETED' ||
      s === 'ERROR' ||
      s === 'STOPPED' ||
      s === 'PARTIAL'
    );
  }

  private emptyState(): FlowMonitorState {
    return {
      idjob: null,
      threadName: null,
      idconf: null,
      status: 'IDLE',
      error: false,
      errorMessage: null,
      steps: [],
      rawResult: null,
      startedAt: null,
      finishedAt: null,
      elapsedMs: 0,
      rowsCount: 0,
      title: '',
      contextoAcao: null,
      starting: false,
      jobId: null,
      label: null,
      rowsOk: 0,
      rowsErr: 0,
      strategy: null,
      idexeclog: null,
      readonly: false,
    };
  }
}
